// カートに「別注」商品が混ざっているとき、配送日選択を最短お届け日以降に絞る。
// wuSiteResource2 の Shared/SiteCommon.ts（checkBetchuInCart / hideBetchuShippingDates）から移設。
// カートは2ページに分かれている（実物確認済み・2026-08-25）:
//   /cart          #shoppingcart_page_step0  .cart_data_box の商品一覧（ここで別注の有無を見る）
//   /cart/shipping #shoppingcart_page_step2  .shipping_date_selection（ここで配送日を絞る）
// ページをまたぐので Cookie で受け渡す。

const COOKIE_NAME = 'has_betchu'
// 配送日 <option> は本日の7日先から1日刻みで並んでいる（実物確認: 2026-08-25 時点で先頭が9/1）。
const OPTION_START_DAYS = 7
// 別注混在時に選択可能とする最短お届け日（本日からの日数）。3サイト共通(依頼先・リードタイム同一)。
export const BETCHU_EARLIEST_SHIPPING_DAYS = 30

/** 先頭から削る <option> の数。テストしやすいよう日数計算だけ切り出す。 */
export function optionsToRemove(earliestShippingDays: number): number {
  return earliestShippingDays - OPTION_START_DAYS
}

/**
 * option の value（"YYYY-MM-DD"）が today から何日先かを返す。パース失敗なら null。
 * テストしやすいよう today を引数で受ける。
 */
export function daysFromToday(dateStr: string, today: Date): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr)
  if (!m) return null
  const target = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  return Math.round((target.getTime() - base.getTime()) / 86400000)
}

/** カートページ(/cart)で別注商品の有無を判定し、Cookie に保存する。 */
export function checkBetchuInCart(): void {
  let hasBetchu = false
  document.querySelectorAll('.cart_data_box').forEach((box) => {
    box.querySelectorAll('.goods_name, .model_number').forEach((el) => {
      if (el.textContent?.includes('別注')) hasBetchu = true
    })
  })
  document.cookie = hasBetchu ? `${COOKIE_NAME}=1; path=/` : `${COOKIE_NAME}=; path=/; max-age=0`
}

/** 配送日選択ページ(/cart/shipping)で、別注があれば選択可能日を絞る。 */
export function hideBetchuShippingDates(
  select: HTMLSelectElement,
  earliestShippingDays: number = BETCHU_EARLIEST_SHIPPING_DAYS,
): void {
  const hasBetchu = document.cookie.split(';').some((c) => c.trim().startsWith(`${COOKIE_NAME}=1`))
  if (!hasBetchu) return

  // デプロイの過渡期、wuSiteResource2 側の旧実装と同時に本番へ乗る瞬間がありうる
  // （別リポジトリの自動デプロイは同時にならない）。同じ Cookie を見て同じ option を削るので、
  // 両方が動くと総取りで全滅する。先頭 option が既に「本日+OPTION_START_DAYS日」より
  // 後ろにずれていたら、旧実装が既に削った後とみなして何もしない。
  const firstOption = select.querySelector<HTMLOptionElement>('option[value]:not([value=""])')
  if (firstOption) {
    const days = daysFromToday(firstOption.value, new Date())
    if (days !== null && days > OPTION_START_DAYS) return
  }

  const removeDays = optionsToRemove(earliestShippingDays)
  let removed = 0
  for (const option of Array.from(select.querySelectorAll('option'))) {
    if (option.value !== '' && removed < removeDays) {
      option.remove()
      removed++
    }
  }
  if (removed === 0) return

  const firstDateOption = select.querySelector<HTMLOptionElement>('option[value]:not([value=""])')
  if (!firstDateOption) return
  // 例: "2026年9月1日 (火)" → 曜日を除去
  const newStartDate = (firstDateOption.textContent ?? '').replace(/\s*\(.\)\s*$/, '')

  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT)
  let node: Node | null
  while ((node = walker.nextNode())) {
    if (node.textContent && /お届け指定可能日は/.test(node.textContent)) {
      node.textContent = node.textContent.replace(
        /お届け指定可能日は\d{4}年\d{1,2}月\d{1,2}日から/,
        `お届け指定可能日は${newStartDate}から`,
      )
    }
  }
}
